import { memo, useMemo, useState } from 'react';
import { ChevronDown, History, MapPin, Tag } from 'lucide-react';
import {
  type StockGroupDto,
  type StockPlaceDto,
} from '@eombor/contracts';
import { Badge, Button, Table, cn, useToast, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { placesByWarehouse } from '../model/stock-places.js';
import { WarehouseStockCard } from './WarehouseStockCard.js';

/**
 * Miqdorni ekranga chiqarish. Yumaloqlash `numeric(14,3)` bilan bir xil —
 * qoldiq HISOBI bu yerda qilinmaydi, faqat ko'rsatiladi.
 */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/**
 * Yig'ilgan holatda ko'rinadigan omborlar soni.
 *
 * Ikkita: mahsulot odatda bir yoki ikki omborda yotadi va shunda tugma
 * umuman chiqmaydi. Beshta ombor bir vaqtda ochilsa, karta ekrandan uzun
 * bo'lib, ro'yxatdagi keyingi mahsulot ko'rinmay qolardi.
 */
const SHOWN_WAREHOUSES = 2;

export interface StockResultRowProps {
  group: StockGroupDto;
  /**
   * Kam qoldiq chegarasi KATALOGDAN keladi: `GET /stock/search` javobida
   * `minStock` yo'q (u nomenklatura xossasi, qoldiq xossasi emas). Ma'lum
   * bo'lmasa belgi umuman chizilmaydi — soxta ogohlantirish bo'lmaydi.
   */
  minStock?: number | null;
  onShowHistory: (place: StockPlaceDto) => void;
  /**
   * «Qayerda turibdi» oynasi — katak kodi bosilganda. Berilmasa kod
   * oddiy matn bo'lib qoladi.
   *
   * NEGA KATAK KODI: aynan shu ustun «qayerda?» degan savolga javob
   * beradi va o'sha javobning ustiga bosish tabiiy harakat. Ro'yxatda
   * yana bitta tugma bo'lsa, u har qatorda takrorlanib, «Tarix» bilan
   * yonma-yon turib, qaysi biri nima qilishini chalkashtirardi.
   */
  onShowLocation?: (place: StockPlaceDto) => void;
  /**
   * ETIKETKA BOSISH — qutiga yopishtiriladigan partiya etiketkasi.
   *
   * Aynan shu qatorda, chunki savol shu yerda tug'iladi: skladchi
   * mahsulotni qidirib topadi va etiketkasi yirtilganini o'sha payt
   * ko'radi. Ruxsati yo'q odamga berilmaydi — u holda tugma umuman
   * chizilmaydi.
   */
  onPrintLabel?: (place: StockPlaceDto) => void;
}

/**
 * Qidiruv natijasining bitta bloki — SKLADCHINING ko'rinishi: tepada
 * nomenklatura va jami miqdorlar, pastda u qaysi ombor, katak va
 * partiyada turgani.
 *
 * Buyurtmachi bu ko'rinishni ko'rmaydi — unga `StockProductCard` beriladi:
 * partiya raqami va katak kodi uning ishi emas va ular asosiy sonni
 * ko'rinmas qilib qo'yardi.
 *
 * QOLDIQ IKKI XIL chiziladi va bu asosiy qaror. Monitorda jadval qoladi:
 * skladchi u yerda o'nlab qatorni ustun bo'yicha solishtiradi. Telefonda
 * esa jadval yaramaydi — 375px ga oltita ustun sig'maydi va eng kerakli
 * son («Mavjud») aynan o'ng chetdan chiqib ketardi. Karta ichidagi
 * gorizontal siljish esa sahifani vertikal aylantirmoqchi bo'lgan barmoq
 * ostida qimirlab, o'qishni buzardi.
 *
 * Mavjud miqdor SERVERDAN keladi (`qty - reservedQty` backendda hisoblanadi)
 * va bu yerda qayta hisoblanmaydi — aks holda ikki raqam ajralib ketardi.
 */
/** `2026-09-12T…` → `12.09`. Yil yozilmaydi: etiketka shu yilniki. */
const dateText = (iso: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}`;
};

const StockResultRowView = ({
  group,
  minStock = null,
  onShowHistory,
  onPrintLabel,
  onShowLocation,
}: StockResultRowProps) => {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);

  const warehouses = useMemo(() => placesByWarehouse(group), [group]);

  const none = group.totalAvailableQty <= 0;
  const low = !none && minStock !== null && group.totalAvailableQty < minStock;
  const reserved = Math.max(0, group.totalQty - group.totalAvailableQty);

  const hiddenCount = Math.max(0, warehouses.length - SHOWN_WAREHOUSES);
  const notifyCopied = (): void => toast.show(t('stock.copied'), 'success');

  const columns: Column<StockPlaceDto>[] = [
    {
      key: 'warehouseName',
      header: t('stock.colWarehouse'),
      render: (place) => <Badge tone="neutral">{place.warehouseName}</Badge>,
    },
    {
      key: 'cellCode',
      header: t('stock.colCell'),
      mono: true,
      width: '10rem',
      render: (place) =>
        onShowLocation === undefined ? (
          place.cellCode
        ) : (
          <button
            type="button"
            onClick={() => onShowLocation(place)}
            title={t('stock.locationOpen')}
            className={cn(
              'flex items-center gap-1 rounded-[var(--radius-sm)] -mx-1 px-1',
              'font-mono text-accent underline-offset-2 hover:underline',
              'focus-visible:outline-2 focus-visible:outline-offset-1',
              'focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <MapPin size={12} aria-hidden className="shrink-0" />
            {place.cellCode}
          </button>
        ),
    },
    { key: 'batchNo', header: t('stock.colBatch'), mono: true, width: '10rem' },
    {
      key: 'availableQty',
      header: t('stock.colAvailable'),
      align: 'right',
      mono: true,
      width: '9rem',
      render: (place) => qtyText(place.availableQty),
    },
    {
      key: 'reservedQty',
      header: t('stock.colReserved'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (place) =>
        place.reservedQty === 0 ? (
          <span className="text-text-faint">—</span>
        ) : (
          qtyText(place.reservedQty)
        ),
    },
    {
      key: 'label',
      header: t('stock.colLabel'),
      width: '9rem',
      /* Bosilgan FAKTI ko'rsatiladi, yopishtirilgani emas: buni tizim
         bilolmaydi va bilgandek ko'rsatish yolg'on bo'lardi. */
      render: (place) =>
        place.labelPrintedAt === null ? (
          <Badge tone="warn">{t('stock.labelNone')}</Badge>
        ) : (
          <Badge tone="success">{t('stock.labelDone', { date: dateText(place.labelPrintedAt) })}</Badge>
        ),
    },
    {
      key: 'actions',
      header: t('stock.colActions'),
      align: 'right',
      width: '13rem',
      /* Ilgali bu yerda O'CHIRILGAN «Buyurtma» tugmasi ham turardi —
         u yo'l borligini aytardi, lekin bosilmasdi va har qatorda
         takrorlanardi. Endi buyurtma buyurtmachining kartochkasida
         (`StockProductCard`), tugma esa savat qayerda bo'lsa o'sha yerda. */
      render: (place) => (
        <span className="flex items-center justify-end gap-1">
          {onPrintLabel === undefined ? null : (
            /*
             * ETIKETKA tugmasi «Tarix» dan KUCHLIROQ va bu ataylab:
             * etiketkasiz qutini skaner berish paytida tanimaydi, ya'ni
             * xato kirimda tug'ilib, berishda chiqadi. Hali bosilmagan
             * partiyada tugma yanada ko'rinarli.
             */
            <Button
              variant={place.labelPrintedAt === null ? 'secondary' : 'ghost'}
              onClick={() => onPrintLabel(place)}
            >
              <Tag size={16} aria-hidden />
              {t('stock.label')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onShowHistory(place)}>
            <History size={14} aria-hidden />
            {t('stock.history')}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <li
      className={cn(
        'flex flex-col gap-4 rounded-[var(--radius-lg)] border border-border',
        'bg-surface p-4 shadow-[var(--shadow-1)]',
        'transition-[box-shadow,transform] duration-[var(--motion-base)] ease-[var(--ease-standard)]',
        // Ustiga olib borish FAQAT sichqonchali qurilmada: teginish
        // ekranida "hover" bosishdan keyin yopishib qolardi.
        '[@media(hover:hover)]:hover:-translate-y-px',
        '[@media(hover:hover)]:hover:shadow-[var(--shadow-2)]',
      )}
    >
      <header className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          {/* Nom KESILMAYDI: `truncate` bu yerda uzun nomenklatura nomini
              yarmida uzib qo'yardi va ikki o'xshash mahsulot ekranda bir
              xil ko'rinardi. */}
          <h2 className="min-w-0 text-base leading-snug font-semibold break-words text-text">
            {group.name}
          </h2>
          <span className="flex flex-wrap items-center gap-2">
            <span className="tabular text-xs break-all text-text-faint">{group.code}</span>
            {/* Rang FAQAT belgida: karta chetini bo'yash ro'yxatni
                svetofor qilib yuborardi va ko'z qaysi kartani birinchi
                o'qishni bilmasdi. */}
            {none ? <Badge tone="danger">{t('stock.outOfStock')}</Badge> : null}
            {low ? <Badge tone="warn">{t('stock.lowStock')}</Badge> : null}
          </span>
        </div>
      </header>

      {/*
        IKKI JAMI RAQAM — teng huquqli.
        Ilgari ular yonma-yon turardi va «Jami qoldiq» kichikroq shriftda
        edi: ko'z uni izoh deb o'qirdi, holbuki bu boshqa savolga javob —
        "omborda umuman qancha yotibdi?".
      */}
      <div
        data-totals
        className="grid grid-cols-2 divide-x divide-border rounded-[var(--radius-md)] bg-surface-2"
      >
        <Total
          label={t('stock.totalAvailable')}
          qty={group.totalAvailableQty}
          unit={group.unit}
          dim={none}
        />
        <Total label={t('stock.totalQty')} qty={group.totalQty} unit={group.unit} />
      </div>

      {reserved > 0 ? (
        <p className="tabular -mt-2 text-xs text-text-faint">
          {t('stock.reservedOf', {
            reserved: qtyText(reserved),
            total: qtyText(group.totalQty),
            unit: unitText(group.unit),
          })}
        </p>
      ) : null}

      {warehouses.length === 0 ? (
        /* Bo'sh jadval sarlavhasi "ustunlar bor, qatorlar yo'q" degan
           chalkash xabar berardi — javob bitta jumla bo'lishi kerak. */
        <p className="py-6 text-center text-sm text-text-muted">{t('stock.noWarehouses')}</p>
      ) : desktop ? (
        <Table
          caption={t('stock.placesCaption', { name: group.name })}
          density="compact"
          layout="fixed"
          columns={columns}
          rows={group.places}
          rowKey={(place) => `${place.batchId}:${place.cellId}`}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {/* Planshetda ikki ustun: 768px da blok butun enni egallashi
              shart emas va ikkinchi ombor ham ekranga sig'adi. */}
          <ul className="grid gap-3 md:grid-cols-2">
            {warehouses.slice(0, SHOWN_WAREHOUSES).map((row) => (
              <WarehouseStockCard
                key={row.warehouseId}
                row={row}
                unit={group.unit}
                onCopied={notifyCopied}
              />
            ))}
          </ul>

          {/* Qolganlari faqat OCHIQ holatda chiziladi — yopiq turgan
              o'nlab blok ro'yxatni sekinlashtirardi va ekran o'qigich
              ularni baribir o'qib ketardi. */}
          {expanded ? (
            <div className="motion-tree-in grid">
              <div className="overflow-hidden">
                <ul className="grid gap-3 md:grid-cols-2">
                  {warehouses.slice(SHOWN_WAREHOUSES).map((row) => (
                    <WarehouseStockCard
                      key={row.warehouseId}
                      row={row}
                      unit={group.unit}
                      onCopied={notifyCopied}
                    />
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          {hiddenCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
              className="self-start"
            >
              <ChevronDown
                size={14}
                aria-hidden
                className={cn(
                  'transition-transform duration-[var(--motion-slow)] ease-[var(--ease-standard)]',
                  expanded ? 'rotate-180' : undefined,
                )}
              />
              {expanded
                ? t('stock.showLessWarehouses')
                : t('stock.showMoreWarehouses', { count: hiddenCount })}
            </Button>
          ) : null}
        </div>
      )}
    </li>
  );
};

/**
 * Ro'yxat uzun bo'ladi (100+ mahsulot) va kartaning kirish ma'lumoti
 * o'zgarmaydi: qidiruv javobi kelgach `group` bir xil obyekt bo'lib
 * qolaveradi. Shu sababli qayta chizish faqat haqiqatan o'zgarganda
 * bo'lsin.
 */
export const StockResultRow = memo(StockResultRowView);

interface TotalProps {
  label: string;
  qty: number;
  unit: StockGroupDto['unit'];
  /** Qoldiq yo'q — raqam xira bo'ladi, lekin yashirilmaydi. */
  dim?: boolean;
}

/** Kichik yorliq tepada, katta raqam pastda — ikkalasi bir ustunda. */
const Total = ({ label, qty, unit, dim = false }: TotalProps) => (
  <div className="flex min-w-0 flex-col gap-1 px-4 py-3">
    <span className="text-xs text-text-faint">{label}</span>
    <span className="flex flex-wrap items-baseline gap-1.5">
      <span
        className={cn('tabular text-2xl leading-none font-bold', dim ? 'text-text-faint' : 'text-text')}
      >
        {qtyText(qty)}
      </span>
      <span className="text-xs text-text-muted">{unitText(unit)}</span>
    </span>
  </div>
);
